import db from './database'
import {
  MenuItem,
  Order,
  ComplaintFeedback,
  PromoIncentive,
  User,
  OrderDetails,
} from '../types/database'
export const AdminService = {
  // User Operations
  async getUsers(): Promise<User[]> {
    return await db.users.toArray()
  },
  // Menu Item Operations
  async getMenuItems(): Promise<MenuItem[]> {
    return await db.menuItems.toArray()
  },
  async addMenuItem(item: Omit<MenuItem, 'ItemID'>): Promise<number> {
    const lastItem = await db.menuItems.orderBy('ItemID').last()
    const newItemId = lastItem ? lastItem.ItemID + 1 : 1
    return await db.menuItems.add({
      ...item,
      ItemID: newItemId,
    })
  },
  async updateMenuItem(item: MenuItem): Promise<number> {
    return await db.menuItems.update(item.ItemID, item)
  },
  async deleteMenuItem(itemId: number): Promise<void> {
    await db.menuItems.delete(itemId)
  },
  // Order Operations
  async getOrders(): Promise<Order[]> {
    return await db.orders.toArray()
  },
  async getOrderDetails(orderId: string): Promise<any> {
    const order = await db.orders.get(orderId)
    if (!order) return null
    const details = await db.orderDetails
      .where('Order_ID')
      .equals(orderId)
      .toArray()
    const itemsPromises = details.map(async (detail) => {
      const menuItem = await db.menuItems.get(detail.Item_ID)
      return {
        ...detail,
        name: menuItem?.Item_Name || 'Unknown Item',
        price: menuItem?.Price || 0,
      }
    })
    const items = await Promise.all(itemsPromises)
    return {
      ...order,
      items,
    }
  },
  async updateOrderStatus(
    orderId: string,
    status: Order['Order_Status'],
  ): Promise<number> {
    return await db.orders.update(orderId, { Order_Status: status })
  },
  async addOrderItem(
    orderId: string,
    itemId: number,
    quantity: number,
  ): Promise<void> {
    const menuItem = await db.menuItems.get(itemId)
    if (!menuItem) throw new Error('Menu item not found')
    await db.orderDetails.add({
      Order_ID: orderId,
      Item_ID: itemId,
      Quantity: quantity,
      Subtotal: menuItem.Price * quantity,
    })
    await this.recalculateOrderTotal(orderId)
  },
  async updateOrderItem(
    orderDetailId: number,
    quantity: number,
  ): Promise<void> {
    const detail = await db.orderDetails.get(orderDetailId)
    if (!detail) throw new Error('Order detail not found')
    const menuItem = await db.menuItems.get(detail.Item_ID)
    if (!menuItem) throw new Error('Menu item not found')
    await db.orderDetails.update(orderDetailId, {
      Quantity: quantity,
      Subtotal: menuItem.Price * quantity,
    })
    await this.recalculateOrderTotal(detail.Order_ID)
  },
  async deleteOrderItem(orderDetailId: number): Promise<void> {
    const detail = await db.orderDetails.get(orderDetailId)
    if (!detail) throw new Error('Order detail not found')
    const orderId = detail.Order_ID
    await db.orderDetails.delete(orderDetailId)
    await this.recalculateOrderTotal(orderId)
  },
  async recalculateOrderTotal(orderId: string): Promise<void> {
    const details = await db.orderDetails
      .where('Order_ID')
      .equals(orderId)
      .toArray()
    const total = details.reduce((sum, detail) => sum + detail.Subtotal, 0)
    await db.orders.update(orderId, { Total_Amount: total })
  },
  // Complaint Operations
  async getComplaints(): Promise<ComplaintFeedback[]> {
    return await db.complaints.toArray()
  },
  async updateComplaintStatus(
    complaintId: number,
    status: ComplaintFeedback['Status'],
  ): Promise<number> {
    return await db.complaints.update(complaintId, { Status: status })
  },
  // Promotion Operations
  async getPromotions(): Promise<PromoIncentive[]> {
    return await db.promos.toArray()
  },
  async addPromotion(promo: Omit<PromoIncentive, 'PromoID'>): Promise<number> {
    const lastPromo = await db.promos.orderBy('PromoID').last()
    const newPromoId = lastPromo ? lastPromo.PromoID + 1 : 1
    return await db.promos.add({
      ...promo,
      PromoID: newPromoId,
    })
  },
  async updatePromotion(promo: PromoIncentive): Promise<number> {
    return await db.promos.update(promo.PromoID, promo)
  },
  async deletePromotion(promoId: number): Promise<void> {
    await db.promos.delete(promoId)
  },
  // Inventory Operations
  async getInventory(): Promise<any[]> {
    return await db.inventory.toArray()
  },
  async updateInventory(
    inventoryId: number,
    data: Partial<any>,
  ): Promise<number> {
    return await db.inventory.update(inventoryId, data)
  },
  // Dashboard Statistics
  async getDashboardStats(): Promise<any> {
    const orders = await db.orders.toArray()
    const today = new Date().toDateString()
    const todaysOrders = orders.filter(
      (order) => new Date(order.Order_Date).toDateString() === today,
    )
    const pendingOrders = orders.filter(
      (order) => order.Order_Status === 'Pending',
    )
    const totalRevenue = todaysOrders.reduce(
      (sum, order) => sum + order.Total_Amount,
      0,
    )
    const users = await db.users.toArray()
    const todayRegistrations = users.filter(
      (user) =>
        new Date(user.Date_Registered).toDateString() === today &&
        user.User_type === 'customer',
    )
    return {
      todayOrderCount: todaysOrders.length,
      pendingOrderCount: pendingOrders.length,
      totalRevenue,
      newCustomers: todayRegistrations.length,
    }
  },
}
export default AdminService
